import React from 'react';
import { Button, DataTable, SparkIcon, Text, TooltipTrigger, TruncatedText } from '@spark';
import { formatDate, formatDateTime } from '@/shared/utils/helpers';
import { formatScore } from '@/shared/utils/metricFormatting';
import { getUserFacingDatasetName } from '@/features/datasets/utils/datasetCloneNames';
import '@/app/styles/scrollbar.css';
import BenchmarkVersionExpandedDetails from './BenchmarkVersionExpandedDetails';

/** Split "Name v1.0" into { name, version } or return single line. */
function parseBenchmarkDisplayName(displayName) {
  if (!displayName || typeof displayName !== 'string') return { name: displayName || '—', version: null };
  const match = displayName.match(/^(.+?)\s+v(\d+(?:\.\d+)?)$/i);
  if (match) {
    const rawName = match[1].trim();
    return { name: getUserFacingDatasetName(rawName) || rawName, version: `v${match[2]}` };
  }
  return { name: getUserFacingDatasetName(displayName) || displayName, version: null };
}

/** Format metric value 0–1 as percentage, or raw number; null/NaN → "—". */
function formatSummarizationMetric(value) {
  if (value == null || Number.isNaN(value)) return '—';
  const n = Number(value);
  if (n <= 1 && n >= 0) return `${(n * 100).toFixed(1)}%`;
  return String(n);
}

/** Parse created_at to timestamp for reliable descend sort (newest first). */
function createdAtTimestamp(record) {
  const v = record?.created_datetime ?? record?.createdDateTime ?? '';
  if (v == null || v === '') return 0;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function CreatedAtCell({ value }) {
  const dateStr = formatDate(value);
  const exactDateTime = formatDateTime(value);
  if (!dateStr) {
    return (
      <Text variant="base-regular" color="primary" as="span">
        —
      </Text>
    );
  }
  return (
    <TooltipTrigger text={exactDateTime || dateStr} placement="top">
      <Text variant="base-regular" color="primary" as="span">
        {dateStr}
      </Text>
    </TooltipTrigger>
  );
}

function VsTeacherCell({ value, vsPct, vsDirection, showVsTeacher, teacherCancelled, teacherName }) {
  const showLine2 = showVsTeacher !== false;
  const hasPct = vsPct != null && !Number.isNaN(Number(vsPct));
  const arrow = vsDirection === 'up' ? '↑ ' : vsDirection === 'down' ? '↓ ' : '';
  const displayPct = hasPct ? `${Number(vsPct)}%` : '—';
  // A cancelled teacher evaluation never produces metrics, so replace the
  // dangling "— vs Teacher" with an explicit "Teacher cancelled" marker.
  const vsLabel = teacherName || 'Teacher';
  const line2Text =
    !hasPct && teacherCancelled ? 'Teacher cancelled' : `${arrow}${displayPct} vs ${vsLabel}`;
  return (
    <div className="spark-datatable-two-line">
      <Text variant="base-regular" color="primary" as="div">
        {formatScore(value) ?? '—'}
      </Text>
      {showLine2 ? (
        // Long teacher names must not wrap the "vs …" line: keep it on one
        // line, ellipsize the overflow, and reveal the full text on hover.
        <TruncatedText text={line2Text} maxWidth="100%">
          <Text variant="detail-regular" color="tertiary" as="span">
            {line2Text}
          </Text>
        </TruncatedText>
      ) : null}
    </div>
  );
}

const SUMMARIZATION_CLASSIC_KEYS = [
  'ROUGE_1', 'ROUGE_2', 'ROUGE_L', 'BLEU', 'METEOR', 'BERTScore', 'CompressionRatio',
];
const SUMMARIZATION_LLM_JUDGE_KEYS = [
  'factuality', 'completeness', 'conciseness', 'coherence', 'compression',
];
const CONTENT_GENERATION_CLASSIC_KEYS = [
  'TEXT_MATCH', 'MIN_HASH', 'ROUGE_1', 'ROUGE_2', 'ROUGE_L', 'BLEU', 'METEOR', 'BERTScore',
];
const CONTENT_GENERATION_LLM_JUDGE_KEYS = ['factuality', 'coherence'];

const CLASSIC_KEY_LABELS = {
  TEXT_MATCH: 'Exact Match',
  MIN_HASH: 'Fuzzy Similarity',
};

export default function ModelDetailsQualityBenchmarksTable({
  entries,
  taskType,
  selectedRowKey,
  onSelectRow,
  onDownload,
  /**
   * Display name of the teacher model the "vs" metric lines compare against,
   * e.g. "o3 (AzureAI)". Falls back to the generic "Teacher" when absent.
   */
  teacherName = null,
}) {
  const [createdAtSortOrder, setCreatedAtSortOrder] = React.useState('descend');

  const isContentGeneration = taskType === 'ContentGeneration';
  const isSummarization = taskType === 'Summarization' || isContentGeneration;
  const classicKeys = isContentGeneration
    ? CONTENT_GENERATION_CLASSIC_KEYS
    : SUMMARIZATION_CLASSIC_KEYS;
  const judgeKeys = isContentGeneration
    ? CONTENT_GENERATION_LLM_JUDGE_KEYS
    : SUMMARIZATION_LLM_JUDGE_KEYS;

  const cycleCreatedAtSort = React.useCallback(() => {
    setCreatedAtSortOrder((prev) => {
      if (prev === 'descend') return null;
      if (prev == null) return 'ascend';
      return 'descend';
    });
  }, []);

  const columns = React.useMemo(() => {
    const metricWidth = isSummarization ? 92 : 142;
    const benchmarkCol = {
      title: 'Benchmark',
      dataIndex: 'benchmark_display_name',
      key: 'benchmark_display_name',
      minWidth: isSummarization ? 200 : 126,
      flex: 1,
      render: (text) => {
        const { name, version } = parseBenchmarkDisplayName(text);
        return (
          <div className="spark-datatable-two-line">
            <TruncatedText
              text={name || '—'}
              tooltipText={name || '—'}
              maxWidth="100%"
            />
            {version ? (
              <Text variant="detail-regular" color="tertiary" as="div">
                {version}
              </Text>
            ) : null}
          </div>
        );
      },
    };

    const base = [
      benchmarkCol,
      {
        title: 'Total Examples',
        dataIndex: 'total_examples',
        key: 'total_examples',
        width: isSummarization ? 100 : 107,
        render: (v) => (v != null ? Number(v).toLocaleString() : '—'),
      },
    ];

    if (isSummarization) {
      classicKeys.forEach((key) => {
        base.push({
          title: CLASSIC_KEY_LABELS[key] || key.replace(/_/g, '-'),
          key: `classic_${key}`,
          width: metricWidth,
          render: (_, record) => formatSummarizationMetric(record.metrics_classic?.[key]),
        });
      });
      judgeKeys.forEach((key) => {
        base.push({
          title: key.charAt(0).toUpperCase() + key.slice(1),
          key: `llm_${key}`,
          width: metricWidth,
          render: (_, record) => formatSummarizationMetric(record.metrics_llm_as_a_judge?.[key]),
        });
      });
      base.push({
        title: 'Overall (LLM)',
        key: 'overall_llm_as_a_judge_score',
        width: metricWidth,
        render: (_, record) => formatSummarizationMetric(record.overall_llm_as_a_judge_score),
      });
    } else {
      base.push(
        {
          title: 'F1',
          key: 'f1',
          width: metricWidth,
          render: (_, record) => (
            <VsTeacherCell
              value={record.f1}
              vsPct={record.vs_teacher_f1_pct}
              vsDirection={record.vs_teacher_f1_direction}
              showVsTeacher={record.show_vs_teacher}
              teacherCancelled={record.teacher_cancelled}
              teacherName={teacherName}
            />
          ),
        },
        {
          title: 'Accuracy',
          key: 'accuracy',
          width: metricWidth,
          render: (_, record) => (
            <VsTeacherCell
              value={record.accuracy}
              vsPct={record.vs_teacher_accuracy_pct}
              vsDirection={record.vs_teacher_accuracy_direction}
              showVsTeacher={record.show_vs_teacher}
              teacherCancelled={record.teacher_cancelled}
              teacherName={teacherName}
            />
          ),
        },
        {
          title: 'Precision',
          key: 'precision',
          width: metricWidth,
          render: (_, record) => (
            <VsTeacherCell
              value={record.precision}
              vsPct={record.vs_teacher_precision_pct}
              vsDirection={record.vs_teacher_precision_direction}
              showVsTeacher={record.show_vs_teacher}
              teacherCancelled={record.teacher_cancelled}
              teacherName={teacherName}
            />
          ),
        },
        {
          title: 'Recall',
          key: 'recall',
          width: metricWidth,
          render: (_, record) => (
            <VsTeacherCell
              value={record.recall}
              vsPct={record.vs_teacher_recall_pct}
              vsDirection={record.vs_teacher_recall_direction}
              showVsTeacher={record.show_vs_teacher}
              teacherCancelled={record.teacher_cancelled}
              teacherName={teacherName}
            />
          ),
        },
      );
    }

    base.push(
      {
        title: (
          <Text variant="uniq-semibold-table-caps" color="primary" as="span" onClick={cycleCreatedAtSort}>
            Created At
          </Text>
        ),
        dataIndex: 'created_datetime',
        key: 'created_datetime',
        width: isSummarization ? 110 : 155,
        render: (v, record) => (
          <CreatedAtCell value={v ?? record?.createdDateTime} />
        ),
      },
      {
        title: '',
        key: 'actions',
        width: 48,
        render: (_, record) => (
          <Button
            variant="ghost"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDownload?.(record);
            }}
            aria-label="Download"
            icon={<SparkIcon slug="download" size="small" />}
          />
        ),
      },
    );

    return base;
  }, [isSummarization, classicKeys, judgeKeys, onDownload, cycleCreatedAtSort, teacherName]);

  const dataSource = React.useMemo(() => {
    const rows = (entries || []).map((row, index) => ({
      ...row,
      _id: row.distillation_event_id || String(index),
    }));
    if (createdAtSortOrder == null) return rows;
    const desc = createdAtSortOrder === 'ascend' ? 1 : -1;
    return [...rows].sort(
      (a, b) => desc * (createdAtTimestamp(a) - createdAtTimestamp(b)),
    );
  }, [entries, createdAtSortOrder]);

  return (
    <DataTable
      columns={columns}
      dataSource={dataSource}
      rowKey="_id"
      selectedRowKey={selectedRowKey}
      onRowClick={(record) => onSelectRow?.(record)}
      expandedRowRender={(record) => (
        <BenchmarkVersionExpandedDetails record={record} taskType={taskType} />
      )}
    />
  );
}
